import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mic, Calendar, MessageCircle, Lock, Sparkles, TrendingUp, Shield } from 'lucide-react';
import ProgressButton from './components/ProgressButton';
import FloatingMascot from './components/FloatingMascot';
import BackgroundTetris from './components/BackgroundTetris';

interface EarlyProgressData {
  totalMessages: number;
  totalDaysPracticing: number;
  weeklyStats: {
    messages: number;
    daysActive: number;
    estimatedMinutes: number;
  };
  estimatedMinutesTotal: number;
}

interface EarlyProgressProps {
  data: EarlyProgressData;
  userName: string;
}

function EarlyProgress({ data, userName }: EarlyProgressProps) {
  const navigate = useNavigate();

  // Dados reais do usuário (API /progress): min e dias desta semana + totais
  const weekMinutes = data.weeklyStats?.estimatedMinutes ?? 0;
  const weekDays = data.weeklyStats?.daysActive ?? 0;
  const totalMinutes = data.estimatedMinutesTotal ?? 0;
  const totalDays = data.totalDaysPracticing ?? 0;

  const encouragements = [
    'Mesmo com pouco tempo, deu pra ver que você não tem medo de tentar.',
    'Mesmo com poucas conversas, você já se permitiu falar.',
    'Você já deu o passo mais difícil: começar a falar.',
  ];

  const lockedFeatures = [
    { icon: Sparkles, text: 'Feedbacks mais completos' },
    { icon: TrendingUp, text: 'Habilidades que você já consegue' },
    { icon: Shield, text: 'Seu nível de confiança ao falar' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0a2e] via-[#16082a] to-[#0f0518] relative overflow-x-hidden">
      <BackgroundTetris />

      <div className="progress-inner">

        {/* Header: mesmo layout em web e mobile */}
        <motion.header
          className="progress-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex-shrink-0 pt-1">
            <FloatingMascot size="lg" />
          </div>
          <div>
            <p className="progress-header-greeting">Olá,</p>
            <h1 className="progress-header-name">{userName}</h1>
          </div>
        </motion.header>

        {/* Seu progresso já começou — título em destaque (igual Progress) */}
        <motion.section
          className="mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <h2 className="progress-section-title">Seu progresso já começou</h2>
          </div>

          <div className="space-y-3">
            {encouragements.map((text, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.15, duration: 0.5, type: 'spring' }}
              >
                <motion.div
                  className="flex items-center gap-4 bg-gradient-to-r from-purple-600/30 to-purple-500/10 backdrop-blur-sm rounded-2xl px-5 py-4 border border-purple-500/30 cursor-pointer"
                  whileHover={{
                    scale: 1.03,
                    borderColor: 'rgba(168, 85, 247, 0.6)',
                    boxShadow: '0 0 20px rgba(168, 85, 247, 0.3)',
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.div
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center shadow-lg shadow-green-500/30 flex-shrink-0"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.5 + index * 0.15, type: 'spring', stiffness: 200 }}
                  >
                    <span className="text-white text-sm">✓</span>
                  </motion.div>
                  <span className="text-white font-medium text-lg flex-1">{text}</span>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Prática da semana — mesmo layout e estilo da Progress */}
        <motion.section
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <h2 className="progress-section-title mb-4">Prática da semana</h2>

          <p className="text-white/70 text-sm mb-3" style={{ fontWeight: 400 }}>
            Dados reais do seu uso esta semana e no total.
          </p>

          <div className="flex gap-3">
            <motion.div
              className="flex-1 relative bg-gradient-to-br from-purple-600/40 to-purple-800/40 backdrop-blur-sm rounded-2xl p-5 border border-purple-500/30 overflow-hidden cursor-pointer"
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-transparent"
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <Mic className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <p className="text-5xl font-bold" style={{ color: '#ffffff' }}>{weekMinutes} min</p>
                <p className="text-base" style={{ color: '#ffffff', fontWeight: 400 }}>falando inglês</p>
              </div>
              <motion.div
                className="absolute bottom-2 right-2 w-7 h-7"
                animate={{ y: [0, 4, 0], rotate: [0, 2, -2, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <img
                  src="/images/progress/monster-model.png"
                  alt=""
                  className="w-full h-full object-contain"
                  style={{ filter: 'hue-rotate(90deg) saturate(1.3)' }}
                />
              </motion.div>
            </motion.div>

            <motion.div
              className="flex-1 relative bg-gradient-to-br from-green-600/40 to-green-800/40 backdrop-blur-sm rounded-2xl p-5 border border-green-500/30 overflow-hidden cursor-pointer"
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-transparent"
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
              />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <p className="text-5xl font-bold" style={{ color: '#ffffff' }}>{weekDays} {weekDays === 1 ? 'dia' : 'dias'}</p>
                <p className="text-base" style={{ color: '#ffffff', fontWeight: 400 }}>nesta semana</p>
              </div>
              <motion.div
                className="absolute bottom-2 right-2 w-7 h-7"
                animate={{ y: [0, 4, 0], rotate: [0, -2, 2, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }}
              >
                <img
                  src="/images/progress/monster-model.png"
                  alt=""
                  className="w-full h-full object-contain"
                  style={{ filter: 'hue-rotate(200deg) saturate(1.2)' }}
                />
              </motion.div>
            </motion.div>
          </div>

          <p className="text-white/80 text-sm mt-3 text-center" style={{ fontWeight: 400 }}>
            No total: <strong style={{ color: '#fff' }}>{totalDays}</strong> {totalDays === 1 ? 'dia' : 'dias'} de prática · <strong style={{ color: '#fff' }}>{totalMinutes} min</strong> falando
          </p>
        </motion.section>

        {/* Continue praticando para desbloquear — estilo igual à seção “Sua confiança” da Progress */}
        <motion.section
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-purple-400" />
            <h2 className="progress-section-title">Continue praticando para desbloquear</h2>
          </div>

          <div className="space-y-3">
            {lockedFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  className="flex items-center gap-4 bg-gradient-to-r from-purple-600/30 to-purple-500/10 backdrop-blur-sm rounded-2xl px-5 py-4 border border-purple-500/30"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1, type: 'spring' }}
                >
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center border border-white/20 flex-shrink-0">
                    <Icon className="w-4 h-4 text-white/70" strokeWidth={2} />
                  </div>
                  <span className="text-white font-medium text-lg flex-1">{feature.text}</span>
                  <Lock className="w-4 h-4 text-white/40" />
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        {/* Dica + CTA — mesmo estilo da Progress (bloco dica + botão) */}
        <motion.section
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
        >
          <motion.div
            className="relative bg-gradient-to-br from-purple-900/40 to-indigo-900/20 backdrop-blur-sm rounded-2xl p-5 border border-purple-500/30 overflow-hidden mb-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl" />
            <div className="relative z-10">
              <p className="text-lg font-bold tracking-wide mb-2" style={{ color: '#b076d4', textShadow: '0 0 12px rgba(176, 118, 212, 0.4)' }}>💡 Dica pra você agora:</p>
              <p className="text-sm leading-relaxed" style={{ color: '#ffffff', fontWeight: 400 }}>
                Fale um pouquinho todos os dias. Com mais conversas, o Ataric consegue te dar feedbacks ainda melhores.
              </p>
            </div>
          </motion.div>

          <ProgressButton
            variant="cta"
            size="xl"
            className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 shadow-lg shadow-purple-500/30"
            onClick={() => navigate('/')}
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Falar inglês agora
          </ProgressButton>
        </motion.section>
      </div>
    </div>
  );
}

export default EarlyProgress;
